import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Download,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  Search,
} from "lucide-react";
import * as z from "zod";
import { HTTPError } from "ky";
import type { UtteranceHistoryEntry } from "../meetings/-hooks";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/clipboard-buttons";
import { api } from "@/lib/http";
import { userInfoQueryOptions } from "@/lib/queries/auth";

export const Route = createFileRoute("/_app/admin-utterances/")({
  component: RouteComponent,
  async beforeLoad({ context: { queryClient } }) {
    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);
    if (!userInfo.is_admin) throw redirect({ to: "/" });
  },
});

const utteranceIdSchema = z.uuid();

function formatTime(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function RouteComponent() {
  const [utteranceId, setUtteranceId] = useState("");

  const [error, setError] = useState<string | null>(null);

  const utteranceDetails = useMutation({
    async mutationFn(utteranceId: string) {
      try {
        return await api.processing.raw
          .get<UtteranceHistoryEntry>(`api/v1/utterances/${utteranceId}`)
          .json();
      } catch (e) {
        if (!(e instanceof HTTPError)) throw e;

        const data = await e.response.json();
        if ("detail" in data && typeof data.detail === "string") {
          throw new Error(data.detail);
        } else throw e;
      }
    },
  });

  const submit = useCallback(
    (utteranceId: string) => {
      const validation = utteranceIdSchema.safeParse(utteranceId.trim());
      if (!validation.success) setError(z.prettifyError(validation.error));
      else utteranceDetails.mutate(validation.data);
    },
    [utteranceDetails.mutate, utteranceIdSchema],
  );

  const handleReset = () => {
    utteranceDetails.reset();
    setError(null);
    setUtteranceId("");
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Admin Utterance Viewer
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Search and analyze utterances by ID
        </p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Input
            type="text"
            placeholder="Enter Utterance ID..."
            value={utteranceId}
            onChange={(e) => {
              setError(null);
              setUtteranceId(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                submit(utteranceId);
              }
            }}
            className="pr-10"
          />
        </div>
        <Button
          onClick={() => submit(utteranceId)}
          disabled={utteranceDetails.isPending || !utteranceId.trim()}
        >
          {utteranceDetails.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search className="h-4 w-4" />
              Search
            </>
          )}
        </Button>
        {!utteranceDetails.isIdle && (
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="mr-1 h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      {utteranceDetails.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {utteranceDetails.error instanceof Error
            ? utteranceDetails.error.message
            : "Failed to fetch utterance"}
        </div>
      )}
      {error !== null && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 whitespace-pre-wrap text-destructive">
          {error}
        </div>
      )}

      {utteranceDetails.data && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Audio Transcription</CardTitle>
                <CopyButton
                  text={utteranceDetails.data.transcription_text}
                  variant="outline"
                  size="sm"
                  successChildren={
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  }
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </CopyButton>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
                    {utteranceDetails.data.transcription_text}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Audio Player Card */}
            {utteranceDetails.data.storage_path && (
              <AudioPlayer
                key={utteranceDetails.data.id}
                storagePath={utteranceDetails.data.storage_path}
                duration={utteranceDetails.data.duration_ms}
              />
            )}
          </div>

          {/* Right Column - AI Results JSON */}
          <div className="lg:col-span-1">
            <Card className="h-full">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Vexon Result (JSON)</CardTitle>
                <CopyButton
                  text={JSON.stringify(
                    utteranceDetails.data.ai_results.voice_authentication,
                    null,
                    2,
                  )}
                  variant="outline"
                  size="sm"
                  successChildren={
                    <>
                      <Check className="h-4 w-4" />
                      Copied
                    </>
                  }
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </CopyButton>
              </CardHeader>
              <CardContent>
                <div className="max-h-[500px] overflow-auto rounded-lg border border-border bg-slate-950 p-4">
                  <code className="text-xs whitespace-pre text-green-400">
                    {JSON.stringify(
                      utteranceDetails.data.ai_results.voice_authentication,
                      null,
                      2,
                    )}
                  </code>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {utteranceDetails.isIdle && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 py-16">
          <div className="mb-4 rounded-full bg-muted p-4">
            <Search className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-medium text-foreground">
            No Utterance Selected
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Enter an utterance ID above to view its details
          </p>
        </div>
      )}
    </div>
  );
}

function AudioPlayer({
  storagePath,
  duration,
}: {
  storagePath: string;
  duration: number;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const togglePlayback = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };
  return (
    <Card>
      <CardContent className="pt-6">
        <audio
          ref={audioRef}
          src={storagePath}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleAudioEnded}
          className="hidden"
        />
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={togglePlayback}
            className="flex items-center gap-2"
          >
            {isPlaying ? (
              <>
                <Pause className="h-5 w-5" />
                Pause Audio
              </>
            ) : (
              <>
                <Play className="h-5 w-5" />
                Play Audio
              </>
            )}
          </Button>

          <div className="flex flex-1 items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {formatTime(currentTime)}
            </span>
            <div className="relative h-2 flex-1 rounded-full bg-muted">
              <div
                className="absolute left-0 h-full rounded-full bg-primary transition-all"
                style={{
                  width: `${(currentTime / (duration / 1000)) * 100}%`,
                }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {formatTime(duration / 1000)}
            </span>
          </div>

          <a
            target="_blank"
            download
            href={storagePath}
            className={buttonVariants({ variant: "outline", size: "icon" })}
            title="Download Audio"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}
